import { wishlistItemBody } from './wishlistItemBody.mjs'

export const WISHLIST_HANDLE = 'wishlist'
export const ASSIGNED_HANDLE = 'assigned'

/** "Leo" -> "Leo's Wishlist"; "James" -> "James' Wishlist". */
export const childListName = (firstName) => {
  if (!firstName) return 'Wishlist'
  return `${firstName}${firstName.endsWith('s') ? "'" : "'s"} Wishlist`
}

/** Excludes the 'assigned' shoe-wardrobe list. Inclusion-filtering would hide legacy lists, which carry no handle. */
const isWishlistList = (list) => list?.handle !== ASSIGNED_HANDLE

/**
 * Every child is read through its own scoped profile, so these run concurrently
 * without the scope leaking between them.
 *
 * A child with no wishlist yet (new child, or one holding only the 'assigned'
 * shoe-wardrobe list) still needs a row in the drawer, so one is created here
 * rather than dropping the child from the results.
 */
const readChildList = async (child, createProfile) => {
  try {
    const profile = createProfile(child.id)
    const [lists, profileData] = await Promise.all([
      profile.getLists(),
      profile.get()
    ])
    const candidates = (lists ?? []).filter(isWishlistList)
    const list = candidates.find((entry) => entry?.handle === WISHLIST_HANDLE) ?? candidates[0] ??
      await profile.createList({ name: WISHLIST_HANDLE, handle: WISHLIST_HANDLE })

    const connector = { ...profileData, ...child.connector }
    return {
      ...list,
      name: childListName(connector.first_name),
      profile: { ...child, connector }
    }
  } catch (error) {
    console.error(`Failed to read wishlist for child ${child?.id}:`, error)
    return null
  }
}

export const readAllLists = async ({ profileClient, createProfile }) => {
  const [parentLists, children] = await Promise.all([
    profileClient.getWishlists().catch((error) => {
      console.error('Failed to read parent wishlists:', error)
      return []
    }),
    profileClient.getChildren().catch((error) => {
      console.error('Failed to read child profiles:', error)
      return []
    })
  ])

  const childLists = await Promise.all(
    (children ?? []).map((child) => readChildList(child, createProfile))
  )

  return [
    ...(Array.isArray(parentLists) ? parentLists : []).filter(isWishlistList).map((l) => ({ ...l, profile: null })),
    ...childLists.filter(Boolean)
  ]
}

export const createList = async ({ profileClient, name }) =>
  profileClient.createWishlist({ name, handle: WISHLIST_HANDLE })

const runOperation = async ({ profileClient, createProfile, operation }) => {
  const { type, listId, connectionId, sku, itemId, quantity, meta } = operation
  const scoped = connectionId ? createProfile(connectionId) : null

  // The BFF resolves rather than throwing on an HTTP error, so parent-path writes go
  // through writeWishlistItem, which turns a failed response into a rejection. The
  // scoped SDK path already rejects on its own.
  if (type === 'add') {
    return scoped
      ? scoped.createListItem(listId, wishlistItemBody(sku, meta, quantity))
      : profileClient.writeWishlistItem({
        method: 'POST',
        listId,
        body: wishlistItemBody(sku, meta, quantity)
      })
  }
  if (type === 'remove') {
    return scoped
      ? scoped.deleteListItem(listId, itemId)
      : profileClient.writeWishlistItem({ method: 'DELETE', listId, itemId })
  }
  return scoped
    ? scoped.updateListItem(listId, itemId, { quantity })
    : profileClient.writeWishlistItem({ method: 'PUT', listId, itemId, body: { quantity } })
}

/**
 * Operations are independent, so one failure must not strand the others.
 * Callers reconcile from the returned `ok` flags.
 */
export const applyOperations = async ({ profileClient, createProfile, operations }) => {
  // Grouped by list and run in order within a group. A size change is a remove followed by
  // an add on the SAME list: run those concurrently and a failed remove still lets the add
  // through, leaving the list holding two variants of one product — and diffDraft can only
  // ever remove the one it seeded from, so the other is unreachable. Different lists are
  // independent and stay concurrent.
  const byList = new Map()
  operations.forEach((operation) => {
    const key = String(operation.listId)
    if (!byList.has(key)) byList.set(key, [])
    byList.get(key).push(operation)
  })

  const applied = new Map()

  await Promise.all(
    [...byList.values()].map(async (group) => {
      let aborted = false
      for (const operation of group) {
        if (aborted) {
          applied.set(operation, { ...operation, ok: false, skipped: true })
          continue
        }
        try {
          const item = await runOperation({ profileClient, createProfile, operation })
          applied.set(operation, { ...operation, ok: true, item })
        } catch (error) {
          console.error('Wishlist operation failed:', operation, error)
          applied.set(operation, { ...operation, ok: false, error })
          aborted = true
        }
      }
    })
  )

  return operations.map((operation) => applied.get(operation))
}
